/**
 * Client-side subscription / premium state derived from Firestore `userSubscriptions/{userId}`.
 * Only your backend (webhooks, Cloud Functions, Admin SDK) should write that document.
 *
 * Suggested Firestore document fields:
 * - plan: "free" | "premium" | "pro"
 * - status: "none" | "active" | "trialing" | "canceled" | "past_due" | "expired"
 * - premiumExpiresAt: Timestamp | null (end of paid period)
 * - entitlements: string[] (optional; e.g. ["tax_advanced"] or ["all"])
 * - billingProvider: string | null
 * - externalSubscriptionId: string | null
 */

/** @typedef {"free"|"premium"|"pro"} SubscriptionPlan */
/** @typedef {"none"|"active"|"trialing"|"canceled"|"past_due"|"expired"} SubscriptionStatus */

/**
 * @typedef {Object} SubscriptionState
 * @property {SubscriptionPlan} plan
 * @property {SubscriptionStatus} status
 * @property {boolean} isPremium
 * @property {Date|null} premiumExpiresAt
 * @property {string[]} entitlements
 * @property {string|null} billingProvider
 * @property {string|null} externalSubscriptionId
 */

export const DEFAULT_SUBSCRIPTION_STATE = {
  plan: "free",
  status: "none",
  isPremium: false,
  premiumExpiresAt: null,
  entitlements: [],
  billingProvider: null,
  externalSubscriptionId: null,
};

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") return value.toDate();
  return null;
}

function isExpired(expiresAt) {
  const d = toDate(expiresAt);
  if (!d) return false;
  return d.getTime() <= Date.now();
}

/**
 * @param {Record<string, unknown>|null|undefined} raw
 * @returns {SubscriptionState}
 */
export function deriveSubscriptionState(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_SUBSCRIPTION_STATE };
  }

  const plan =
    raw.plan === "premium" || raw.plan === "pro"
      ? raw.plan
      : raw.plan === "free"
        ? "free"
        : "free";

  const status =
    raw.status === "active" ||
    raw.status === "trialing" ||
    raw.status === "canceled" ||
    raw.status === "past_due" ||
    raw.status === "expired" ||
    raw.status === "none"
      ? raw.status
      : "none";

  const premiumExpiresAt = toDate(raw.premiumExpiresAt) ?? null;
  const expiredByDate = premiumExpiresAt ? premiumExpiresAt.getTime() <= Date.now() : false;

  const entitlements = Array.isArray(raw.entitlements)
    ? raw.entitlements.filter((x) => typeof x === "string")
    : [];

  let isPremium = false;
  if (plan === "premium" || plan === "pro") {
    if (status === "active" || status === "trialing") {
      isPremium = !expiredByDate;
    } else if (status === "canceled") {
      isPremium = !!premiumExpiresAt && !isExpired(raw.premiumExpiresAt);
    }
  }

  return {
    plan: isPremium ? plan : "free",
    status,
    isPremium,
    premiumExpiresAt,
    entitlements,
    billingProvider:
      typeof raw.billingProvider === "string" ? raw.billingProvider : null,
    externalSubscriptionId:
      typeof raw.externalSubscriptionId === "string"
        ? raw.externalSubscriptionId
        : null,
  };
}

/**
 * @param {SubscriptionState} state
 * @param {string} featureId
 * @returns {boolean}
 */
export function userHasEntitlement(state, featureId) {
  if (!state?.isPremium) return false;
  const list = state.entitlements || [];
  if (list.length === 0) return true;
  return list.includes("all") || list.includes(featureId);
}
